/**
 * Phone numbers as the API stores them: E.164, "+" then 7 to 15 digits. The app,
 * the WhatsApp screen and the API's `normalizePhone` all read a number this way.
 *
 * Format only, on purpose. Whether a number exists is the carrier's answer, and a
 * stricter rule would refuse the API's test identities, such as +1111222.
 */

const E164 = /^\+[1-9]\d{6,14}$/;

/** Spaces, separators, and the invisible marks a number copied from a contact card carries. */
const FORMATTING = /[\s().\-/\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g;

/** "+1 202 555 0143", "(+1) 202-555-0143" and "0012025550143" as "+12025550143". */
export function normalizePhoneInput(text: string | null | undefined): string {
  const cleaned = (text ?? "").replace(/\(0\)/g, "").replace(FORMATTING, "");
  return cleaned.startsWith("00") ? `+${cleaned.slice(2)}` : cleaned;
}

export const isE164 = (value: string) => E164.test(value);

/** What is wrong with a typed number, or "" when it can be sent. */
export function phoneProblem(text: string | null | undefined): string {
  const value = normalizePhoneInput(text);
  if (!value) return "Enter a phone number.";
  if (isE164(value)) return "";
  if (!/^\+?\d+$/.test(value)) return "Use digits only, with + and the country code.";
  if (!value.startsWith("+")) return "Start with + and the country code, for example +12025550143.";
  return "That is not a full number: + and the country code, then 7 to 15 digits in all.";
}

export interface PhoneEdit {
  value: string | null;
  problem: string;
}

/**
 * The phone field of a member's edit form.
 *
 * Unchanged goes back untouched, so a stored number from before this rule cannot
 * stop an admin saving someone's name. Blank is null: the column is unique, so a
 * second empty string would collide with the first.
 */
export function resolvePhoneEdit(typed: string | null | undefined, stored: string | null | undefined): PhoneEdit {
  const text = (typed ?? "").trim();
  if (text === (stored ?? "").trim()) return { value: stored ?? null, problem: "" };
  if (!text) return { value: null, problem: "" };

  const problem = phoneProblem(text);
  return { value: problem ? text : normalizePhoneInput(text), problem };
}

/**
 * The phone field of a business, a number people dial rather than one we text. It
 * may be national, as Google lists it, so only the shape is checked. An
 * international one is stored as E.164; a national one keeps its formatting. The
 * column is required, so blank stays an empty string.
 */
export function resolveBusinessPhoneEdit(typed: string | null | undefined, stored: string | null | undefined): PhoneEdit {
  const text = (typed ?? "").trim();
  if (text === (stored ?? "").trim()) return { value: stored ?? "", problem: "" };
  if (!text) return { value: "", problem: "" };

  const value = normalizePhoneInput(text);
  if (value.startsWith("+")) {
    const problem = phoneProblem(text);
    return { value: problem ? text : value, problem };
  }
  if (!/^\d{7,15}$/.test(value)) return { value: text, problem: "Use 7 to 15 digits, with + for the country code if you have it." };
  return { value: text, problem: "" };
}
